import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldWarning, Calendar, EnvelopeSimple, FilePdf, PencilSimple, X, Sparkle, CheckCircle } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY = {
  overdue:  { label: 'EN RETARD', bg: '#FEE2E2', color: '#991B1B', border: '#DC2626' },
  urgent:   { label: '< 15 JOURS', bg: '#FEF3C7', color: '#92400E', border: '#F59E0B' },
  soon:     { label: '< 60 JOURS', bg: '#FFEDD5', color: '#9A3412', border: '#FB923C' },
  later:    { label: 'A VENIR',    bg: '#ECFDF5', color: '#065F46', border: '#10B981' },
  no_date:  { label: 'SANS DATE',  bg: '#F3F4F6', color: '#374151', border: '#9CA3AF' },
};

export const RetentionsWidget = ({ wrapStyle = {} }) => {
  const [data, setData] = useState({ items: [], total_to_claim: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, project: null, draft: '', generating: false, sending: false, summary: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/dashboard/retentions-to-claim`, { withCredentials: true });
      setData(r.data || { items: [], total_to_claim: 0, count: 0 });
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openLetter = async (item) => {
    setDrawer({ open: true, project: item, draft: '', generating: true, sending: false, summary: null });
    try {
      const r = await axios.post(`${API_URL}/api/projects/${item.project_id}/retention-claim-letter/generate`, {}, { withCredentials: true });
      setDrawer(d => ({ ...d, draft: r.data?.draft || '', generating: false, summary: { amount: r.data?.retention_amount_ht, pct: r.data?.retention_pct, release_date: r.data?.release_date } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur generation lettre');
      setDrawer(d => ({ ...d, generating: false }));
    }
  };

  const regenerate = async () => {
    if (!drawer.project) return;
    setDrawer(d => ({ ...d, generating: true }));
    try {
      const r = await axios.post(`${API_URL}/api/projects/${drawer.project.project_id}/retention-claim-letter/generate`, {}, { withCredentials: true });
      setDrawer(d => ({ ...d, draft: r.data?.draft || '', generating: false }));
    } catch { toast.error('Erreur'); setDrawer(d => ({ ...d, generating: false })); }
  };

  const sendLetter = async () => {
    if (!drawer.project) return;
    if (!window.confirm('Envoyer cette lettre au client ?')) return;
    setDrawer(d => ({ ...d, sending: true }));
    try {
      // Save current text first (via send endpoint which persists last_text)
      const r = await axios.post(`${API_URL}/api/projects/${drawer.project.project_id}/retention-claim-letter/send`, { body_text: drawer.draft }, { withCredentials: true });
      toast.success(`Envoyee a ${r.data?.to || 'client'}`);
      setDrawer({ open: false, project: null, draft: '', generating: false, sending: false, summary: null });
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Echec d'envoi"); setDrawer(d => ({ ...d, sending: false })); }
  };

  const downloadPdf = async () => {
    if (!drawer.project) return;
    try {
      await axios.post(`${API_URL}/api/projects/${drawer.project.project_id}/retention-claim-letter/save`, { body_text: drawer.draft }, { withCredentials: true });
      const res = await axios.get(`${API_URL}/api/projects/${drawer.project.project_id}/retention-claim-letter/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Erreur PDF'); }
  };

  if (loading) return <div className="card" style={{ ...wrapStyle, padding: 18 }}>Chargement retenues…</div>;
  if (!data.items.length) {
    return (
      <div className="card" style={{ ...wrapStyle, padding: 18 }} data-testid="retentions-widget-empty">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <ShieldWarning size={20} weight="bold" color="#9A3412" />
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Retenues de garantie</h3>
        </div>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#5E5F66' }}>Aucune retenue active sur vos chantiers en cours.</p>
      </div>
    );
  }

  return (
    <>
      <div className="card" style={{ ...wrapStyle, padding: 18 }} data-testid="retentions-widget">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldWarning size={22} weight="bold" color="#9A3412" />
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Retenues de garantie a recuperer</h3>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#5E5F66', fontWeight: 700, letterSpacing: 0.4 }}>Total a recuperer</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#9A3412' }}>{data.total_to_claim.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR HT</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.items.map(it => {
            const u = URGENCY[it.urgency] || URGENCY.no_date;
            return (
              <div key={it.project_id} data-testid={`retention-row-${it.project_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '10px 12px', border: `1px solid ${u.border}`, background: u.bg, borderRadius: 8 }}>
                <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#1A1A1E', fontSize: '0.9rem' }}>{it.project_name}</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: u.color, background: 'white', padding: '2px 8px', borderRadius: 999, border: `1px solid ${u.border}`, textTransform: 'uppercase', letterSpacing: 0.3 }}>{u.label}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#5E5F66', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{it.client_name || 'Client ?'}</span>
                    {it.retention_release_date && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} /> {fmtDateFR(it.retention_release_date)}
                        {it.days_until_release != null && (
                          <span style={{ color: u.color, fontWeight: 700 }}>
                            {it.days_until_release < 0 ? ` (${Math.abs(it.days_until_release)} j de retard)` : ` (J-${it.days_until_release})`}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 800, color: u.color }}>{it.retention_amount_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR</div>
                  <div style={{ fontSize: '0.7rem', color: '#5E5F66' }}>{it.retention_pct}%</div>
                </div>
                <button className="btn btn-primary btn-sm" data-testid={`retention-letter-btn-${it.project_id}`} onClick={() => openLetter(it)} style={{ background: '#9A3412', color: 'white', borderColor: '#9A3412' }}>
                  <Sparkle size={13} /> Lettre IA
                </button>
                <button className="btn btn-success btn-sm" data-testid={`retention-released-btn-${it.project_id}`} onClick={async () => {
                  if (!window.confirm(`Marquer la retenue de ${it.project_name} comme liberee (recue) ?\nElle disparaitra du widget et le rappel sera annule.`)) return;
                  try {
                    await axios.post(`${API_URL}/api/projects/${it.project_id}/retention/mark-released`, {}, { withCredentials: true });
                    toast.success('Retenue marquee comme liberee');
                    fetchData();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                }} style={{ background: '#10B981', color: 'white', borderColor: '#10B981' }} title="Marquer comme liberee (recue)">
                  <CheckCircle size={13} /> Liberee
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Drawer: Letter editor */}
      {drawer.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setDrawer({ open: false, project: null, draft: '', generating: false, sending: false, summary: null })}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 640, background: 'white', height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }} data-testid="retention-letter-drawer">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#9A3412', color: 'white' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>Lettre de reclamation - {drawer.project?.project_name}</h3>
                <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: 2 }}>
                  {drawer.summary && `${drawer.summary.amount?.toLocaleString('fr-FR')} EUR HT (${drawer.summary.pct}%) - liberation : ${drawer.summary.release_date ? fmtDateFR(drawer.summary.release_date) : 'a preciser'}`}
                </div>
              </div>
              <button onClick={() => setDrawer({ open: false, project: null, draft: '', generating: false, sending: false, summary: null })} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} data-testid="retention-letter-close-btn"><X size={22} /></button>
            </div>
            <div style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: '0.8rem', color: '#5E5F66' }}>
                <Sparkle size={14} style={{ verticalAlign: 'middle' }} /> Brouillon genere par Gemini. Vous pouvez modifier librement avant envoi.
              </div>
              {drawer.generating ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5E5F66' }}>Generation en cours…</div>
              ) : (
                <textarea
                  data-testid="retention-letter-textarea"
                  value={drawer.draft}
                  onChange={(e) => setDrawer(d => ({ ...d, draft: e.target.value }))}
                  style={{ flex: 1, minHeight: 380, width: '100%', padding: 14, fontFamily: 'Georgia, serif', fontSize: '0.92rem', lineHeight: 1.55, border: '1px solid #E2E2E6', borderRadius: 8, resize: 'vertical' }}
                />
              )}
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap', background: '#F9FAFB' }}>
              <button className="btn btn-secondary" onClick={regenerate} disabled={drawer.generating} data-testid="retention-letter-regen-btn">
                <Sparkle size={14} /> Regenerer
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={downloadPdf} data-testid="retention-letter-pdf-btn"><FilePdf size={14} /> PDF</button>
                <button className="btn btn-primary" onClick={sendLetter} disabled={drawer.sending || drawer.generating || !drawer.draft.trim()} data-testid="retention-letter-send-btn" style={{ background: '#9A3412', borderColor: '#9A3412' }}>
                  <EnvelopeSimple size={14} /> {drawer.sending ? 'Envoi…' : 'Envoyer au client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RetentionsWidget;
